import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Upload, Save, Loader2, Calendar as CalendarIcon, Settings, RefreshCw } from 'lucide-react';
import { apiClient } from '../../services/api';
import { toast } from '../../hooks/use-toast';

const STATUSES = [
  { v: 'present', l: 'P', color: 'bg-emerald-100 text-emerald-800' },
  { v: 'absent', l: 'A', color: 'bg-red-100 text-red-800' },
  { v: 'half_day', l: 'H', color: 'bg-amber-100 text-amber-800' },
  { v: 'leave', l: 'L', color: 'bg-blue-100 text-blue-800' },
  { v: 'weekoff', l: 'W', color: 'bg-slate-200 text-slate-700' },
  { v: 'holiday', l: 'HOL', color: 'bg-purple-100 text-purple-800' },
];
const statusLabel = (v) => STATUSES.find((s) => s.v === v) || { l: '-', color: 'bg-white' };

const api = {
  list: (y, m) => apiClient.get(`/hr/attendance?year=${y}&month=${m}`).then((r) => r.data),
  upsert: (p) => apiClient.post('/hr/attendance', p).then((r) => r.data),
  bulk: (entries) => apiClient.post('/hr/attendance/bulk', { entries }).then((r) => r.data),
  summary: (y, m) => apiClient.get(`/hr/attendance/summary?year=${y}&month=${m}`).then((r) => r.data),
  getConfig: () => apiClient.get('/hr/attendance/config').then((r) => r.data),
  putConfig: (p) => apiClient.put('/hr/attendance/config', p).then((r) => r.data),
  employees: () => apiClient.get('/hr/employees').then((r) => r.data),
  importBio: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiClient.post('/hr/attendance/import-biometric', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
  },
};

const AttendancePage = () => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [employees, setEmployees] = useState([]);
  const [records, setRecords] = useState({}); // key = user_id|date
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showImport, setShowImport] = useState(false);
  const [showConfig, setShowConfig] = useState(false);

  const daysInMonth = useMemo(() => new Date(year, month, 0).getDate(), [year, month]);
  const days = useMemo(() => Array.from({ length: daysInMonth }, (_, i) => i + 1), [daysInMonth]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, a, s] = await Promise.all([api.employees(), api.list(year, month), api.summary(year, month)]);
      setEmployees(e.data || []);
      const map = {};
      (a.data || []).forEach((r) => { map[`${r.user_id}|${r.date}`] = r; });
      setRecords(map);
      setSummary(s.data || []);
    } finally { setLoading(false); }
  }, [year, month]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (user_id, day, status) => {
    const date = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setRecords({ ...records, [`${user_id}|${date}`]: { user_id, date, status } });
    try { await api.upsert({ user_id, date, status }); }
    catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); load(); }
  };

  const summaryByUid = Object.fromEntries((summary || []).map((s) => [s.user_id, s]));

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="flex items-end justify-between gap-3 flex-wrap mb-4 sm:mb-6">
        <div className="min-w-0">
          <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">Attendance</span>
          <h1 className="mt-2 text-3xl sm:text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>
            Monthly grid
          </h1>
          <p className="mt-1 text-xs sm:text-sm text-[#0b1a2e]/60">Manual entry · biometric CSV import · LWP feeds payroll.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))} data-testid="att-year"
            className="w-24 px-2 py-1.5 border border-slate-300 rounded text-sm" />
          <select value={month} onChange={(e) => setMonth(parseInt(e.target.value, 10))} data-testid="att-month"
            className="px-2 py-1.5 border border-slate-300 rounded text-sm bg-white">
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <option key={m} value={m}>{new Date(2000, m - 1, 1).toLocaleString('default', { month: 'long' })}</option>)}
          </select>
          <button onClick={load} className="p-2 rounded-full border border-[#0f5c3d]/20 hover:bg-[#f7fbf8]" data-testid="att-refresh"><RefreshCw className="w-4 h-4" /></button>
          <button onClick={() => setShowImport(true)} data-testid="att-import-btn" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#0f5c3d] text-white text-xs"><Upload className="w-3.5 h-3.5" /> Import biometric CSV</button>
          <button onClick={() => setShowConfig(true)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#0f5c3d]/20 text-xs"><Settings className="w-3.5 h-3.5" /> Config</button>
        </div>
      </div>
      {loading ? <div className="p-16 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#0f5c3d] inline" /></div> : (
        <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="text-xs">
              <thead className="bg-[#f7fbf8]">
                <tr>
                  <th className="sticky left-0 bg-[#f7fbf8] p-2 text-left uppercase text-[10px] tracking-widest text-[#0b1a2e]/60 min-w-[180px]">Employee</th>
                  {days.map((d) => {
                    const dow = new Date(year, month - 1, d).getDay();
                    return <th key={d} className={`p-1.5 min-w-[38px] text-center ${dow === 0 || dow === 6 ? 'text-red-500' : 'text-[#0b1a2e]/60'}`}>{d}</th>;
                  })}
                  <th className="p-2 uppercase text-[10px] tracking-widest text-[#0b1a2e]/60">P</th>
                  <th className="p-2 uppercase text-[10px] tracking-widest text-[#0b1a2e]/60">A</th>
                  <th className="p-2 uppercase text-[10px] tracking-widest text-[#0b1a2e]/60">H</th>
                  <th className="p-2 uppercase text-[10px] tracking-widest text-[#0b1a2e]/60 text-red-600">LWP</th>
                </tr>
              </thead>
              <tbody>
                {employees.filter((e) => e.hire_date).map((e) => {
                  const s = summaryByUid[e.user_id] || {};
                  return (
                    <tr key={e.user_id} className="border-t border-black/5" data-testid={`att-row-${e.user_id}`}>
                      <td className="sticky left-0 bg-white p-2 border-r border-black/5 min-w-[180px] max-w-[220px]">
                        <div className="text-sm truncate">{e.name || e.email}</div>
                        <div className="text-[10px] text-[#0b1a2e]/50 truncate">{e.employee_code || '-'}</div>
                      </td>
                      {days.map((d) => {
                        const date = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                        const rec = records[`${e.user_id}|${date}`];
                        const meta = rec ? statusLabel(rec.status) : { l: '·', color: 'bg-white hover:bg-slate-100' };
                        return (
                          <td key={d} className="p-0.5 text-center">
                            <select value={rec?.status || ''} onChange={(ev) => setStatus(e.user_id, d, ev.target.value)}
                              data-testid={`att-cell-${e.user_id}-${d}`}
                              className={`w-7 h-7 rounded text-[10px] font-bold border-0 cursor-pointer text-center ${meta.color}`}>
                              <option value="">{meta.l}</option>
                              {STATUSES.map((st) => <option key={st.v} value={st.v}>{st.l}</option>)}
                            </select>
                          </td>
                        );
                      })}
                      <td className="p-2 text-center font-medium text-emerald-700">{s.present || 0}</td>
                      <td className="p-2 text-center font-medium text-red-600">{s.absent || 0}</td>
                      <td className="p-2 text-center font-medium text-amber-600">{s.half_day || 0}</td>
                      <td className="p-2 text-center font-bold text-red-600" data-testid={`lwp-${e.user_id}`}>{s.lwp_days || 0}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="p-3 text-[11px] text-[#0b1a2e]/60 bg-[#f7fbf8] border-t border-black/5 flex items-center gap-4 flex-wrap">
            <span><b>P</b> Present</span><span><b>A</b> Absent</span><span><b>H</b> Half-day</span>
            <span><b>L</b> Leave</span><span><b>W</b> Week-off</span><span><b>HOL</b> Holiday</span>
            <span className="text-red-600 font-medium ml-4">LWP = Absent + 0.5 × Half-day → auto-deducted in payroll run.</span>
          </div>
        </div>
      )}
      {showImport && <ImportModal onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); load(); }} />}
      {showConfig && <ConfigModal onClose={() => setShowConfig(false)} />}
    </div>
  );
};

const ImportModal = ({ onClose, onDone }) => {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const r = await api.importBio(file);
      toast({ title: 'Imported', description: `${r.imported} rows imported, ${r.skipped} skipped` });
      onDone();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Import failed', variant: 'destructive' }); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden">
        <div className="px-5 py-4 bg-[#0b1a2e] text-white"><div className="font-medium">Import biometric CSV</div></div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-[#0b1a2e]/60">CSV columns (any order, case-insensitive): <b>date</b>, <b>employee_code</b> or <b>user_id</b>, <b>punch_in</b>, <b>punch_out</b>, optional <b>status</b>. Compatible with ZKTeco, ESSL, Matrix, Realtime dumps.</p>
          <input type="file" accept=".csv" onChange={(e) => setFile(e.target.files?.[0])} data-testid="att-import-file"
            className="w-full text-sm" />
          <div className="flex gap-2 justify-end">
            <button onClick={onClose} className="px-3 py-1.5 text-sm">Cancel</button>
            <button onClick={upload} disabled={!file || busy} data-testid="att-import-submit"
              className="px-4 py-2 rounded-full bg-[#0f5c3d] text-white text-sm font-semibold disabled:opacity-50">
              {busy && <Loader2 className="w-3.5 h-3.5 inline animate-spin mr-1" />}Upload
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ConfigModal = ({ onClose }) => {
  const [cfg, setCfg] = useState({ country: 'ALL', working_days_per_month: 26, week_offs: [6], half_day_threshold_hours: 4, full_day_threshold_hours: 8 });
  useEffect(() => { api.getConfig().then(setCfg); }, []);
  const save = async () => {
    try { await api.putConfig(cfg); toast({ title: 'Saved' }); onClose(); }
    catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  const toggleDay = (d) => setCfg({ ...cfg, week_offs: cfg.week_offs.includes(d) ? cfg.week_offs.filter((x) => x !== d) : [...cfg.week_offs, d] });
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden">
        <div className="px-5 py-4 bg-[#0b1a2e] text-white"><div className="font-medium">Attendance config</div></div>
        <div className="p-5 space-y-3">
          <label className="block text-[10px] uppercase font-semibold text-[#0b1a2e]/60">Working days/month</label>
          <input type="number" value={cfg.working_days_per_month} onChange={(e) => setCfg({ ...cfg, working_days_per_month: parseInt(e.target.value, 10) })} className="w-full px-3 py-2 border rounded" />
          <label className="block text-[10px] uppercase font-semibold text-[#0b1a2e]/60">Week offs</label>
          <div className="flex gap-1">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d, i) => (
              <button key={i} onClick={() => toggleDay(i)} className={`px-2 py-1 rounded text-xs ${cfg.week_offs.includes(i) ? 'bg-[#0f5c3d] text-white' : 'bg-slate-100'}`}>{d}</button>
            ))}
          </div>
          <label className="block text-[10px] uppercase font-semibold text-[#0b1a2e]/60">Half-day threshold (hours)</label>
          <input type="number" step="0.5" value={cfg.half_day_threshold_hours} onChange={(e) => setCfg({ ...cfg, half_day_threshold_hours: parseFloat(e.target.value) })} className="w-full px-3 py-2 border rounded" />
          <label className="block text-[10px] uppercase font-semibold text-[#0b1a2e]/60">Full-day threshold (hours)</label>
          <input type="number" step="0.5" value={cfg.full_day_threshold_hours} onChange={(e) => setCfg({ ...cfg, full_day_threshold_hours: parseFloat(e.target.value) })} className="w-full px-3 py-2 border rounded" />
          <button onClick={save} className="w-full mt-3 px-4 py-2 rounded-full bg-[#0f5c3d] text-white text-sm">Save</button>
        </div>
      </div>
    </div>
  );
};

export default AttendancePage;
